import type { SequenceTime } from "framer-motion";
import {
  AlarmClock,
  BadgeCheck,
  Heart,
  Phone,
  Pill,
  ShieldAlert,
  Stethoscope,
  UserRound,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import listMom from "../../assets/landing/list-mom.webp";
import { ACCENTS, SECTION_ACCENT, type AccentName } from "../../lib/accents";
import { cn } from "../../lib/cn";
import { Scene } from "./stage/Scene";
import { SplitChars } from "./stage/Split";
import * as fx from "./stage/fx";
import type { DomStep, Timeline } from "./stage/choreography";

interface ListRow {
  title: string;
  detail: string;
  icon: LucideIcon;
  accent: AccentName;
}

/** Every section of a child's profile, each in the colour it wears across the app. */
const ROWS: readonly ListRow[] = [
  { title: "Basics", detail: "Nickname, age, school", icon: UserRound, accent: SECTION_ACCENT.basic },
  { title: "Emergency contacts", detail: "Tap to call, in the right order", icon: Phone, accent: SECTION_ACCENT.emergency },
  { title: "Allergies & action plans", detail: "Severity, symptoms, what to do", icon: ShieldAlert, accent: SECTION_ACCENT.allergies },
  { title: "Medications", detail: "Dose, timing, where it's kept", icon: Pill, accent: SECTION_ACCENT.medications },
  { title: "Routines", detail: "Naps, screen time, bedtime", icon: AlarmClock, accent: SECTION_ACCENT.routines },
  { title: "Feeding & diet", detail: "Favorites, no-gos, how to prep", icon: Utensils, accent: SECTION_ACCENT.feeding },
  { title: "Medical history", detail: "Conditions, doctors, immunizations", icon: Stethoscope, accent: SECTION_ACCENT.medical },
  { title: "Care preferences", detail: "Comfort items and what calms them", icon: Heart, accent: SECTION_ACCENT.care },
];

/**
 * The illustration springs up off the page like a pop-up book. Local to this
 * scene: none of the shared builders lift-and-grow on a spring (and the iris
 * belongs to the hero).
 */
function springUp(target: string, { at }: { at?: SequenceTime } = {}): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], y: [56, 0], scale: [0.86, 1] },
    options: { type: "spring", duration: 0.6, bounce: 0.3, at, opacity: fx.opacityTween(0.2) },
  };
}

/*
 * Signature: "Checklist ticks". The headline types itself out, the copy and
 * illustration follow, the notepad lands, and then the list fills in row by
 * row: the box pops, a tick is drawn through it, the label slides in.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.35, y: 8 }),
  fx.type("[data-fx=title] [data-char]", { at: "-0.2", cps: 44 }),
  fx.fade("[data-fx=sub]", { at: "-0.15" }),
  springUp("[data-fx=mom]", { at: "-0.3" }),
  fx.fade("[data-fx=card]", { y: 28, duration: 0.4, at: "-0.5" }),
  fx.pop("[data-fx=badge]", { duration: 0.45, at: "-0.2" }),
  // Tick, tick, tick: each row starts ~0.19s after the one above it.
  ...ROWS.flatMap((_, i) => [
    fx.pop(`[data-fx=box-${i}]`, { duration: 0.24, from: 0.3, at: i === 0 ? "-0.38" : "-0.22" }),
    fx.draw(`[data-fx=tick-${i}]`, { duration: 0.15, at: "-0.15" }),
    fx.slide(`[data-fx=row-${i}]`, { x: -16, duration: 0.26, at: "-0.1" }),
  ]),
];

export function WhatsOnTheList() {
  return (
    <Scene
      id="features"
      label="What's on the list"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-amber-50 via-white to-sky-50/40"
    >
      {/* Backdrop: butter and sky fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -right-32 -top-32 h-[30rem] w-[30rem] rounded-full bg-amber-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-40 h-[28rem] w-[28rem] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute left-1/3 top-1/3 h-72 w-72 rounded-full bg-rose-100/50 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 content-center items-center gap-7 px-6 pb-10 pt-20 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
        {/* Copy, then (desktop) the family filling in their list */}
        <div className="relative z-10">
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-amber-700">
            What's on the list
          </p>
          <h2
            data-fx="title"
            className="mt-3 font-display text-3xl font-bold leading-[1.1] tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl"
          >
            <SplitChars text="One list. Everything they'll need." />
          </h2>
          <p data-fx="sub" className="mt-4 max-w-lg text-[15px] text-neutral-600 sm:mt-5 sm:text-lg">
            Every child gets a profile caregivers can actually use in a hurry — clear, current, and easy to
            print.
          </p>
          {/* Tucked in beside the notepad, as if she's filling it in. */}
          <div className="-mr-6 mt-2 hidden justify-end lg:flex">
            <div data-fx="mom">
              <div className="animate-float">
                <img
                  src={listMom}
                  alt="A mom and her toddler filling in their list on a phone"
                  width={760}
                  height={950}
                  loading="lazy"
                  draggable={false}
                  className="h-[min(24rem,calc(100dvh-25rem))] w-auto select-none"
                />
              </div>
            </div>
          </div>
        </div>

        <Notepad />
      </div>
    </Scene>
  );
}

/** Ella's list, as a spiral notepad: ruled lines, a margin rule, and a tick per section. */
function Notepad() {
  return (
    <div className="relative mx-auto w-full max-w-md lg:max-w-[30rem]">
      <div
        data-fx="card"
        className="relative rounded-[1.75rem] border border-amber-100 bg-white px-4 pb-2 pt-5 shadow-glow sm:px-5 sm:pb-3"
      >
        {/* Spiral binding */}
        <div aria-hidden="true" className="absolute inset-x-10 -top-2.5 flex justify-between">
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} className="h-5 w-2 rounded-full bg-gradient-to-b from-neutral-200 to-neutral-400 ring-2 ring-white" />
          ))}
        </div>
        {/* Margin rule */}
        <span aria-hidden="true" className="absolute bottom-3 left-[2.85rem] top-[4.6rem] w-px bg-rose-200 sm:left-[3.1rem]" />

        <div className="flex items-center gap-3 border-b border-amber-100 pb-3">
          <span
            aria-hidden="true"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-100 font-display text-base font-bold text-sky-700 sm:h-10 sm:w-10 sm:text-lg"
          >
            E
          </span>
          <p className="min-w-0 flex-1 truncate font-display text-lg font-bold text-neutral-900">Ella's list</p>
          <span
            data-fx="badge"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100"
          >
            <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Updated just now
          </span>
        </div>

        <ul>
          {ROWS.map((row, i) => {
            const Icon = row.icon;
            return (
              <li
                key={row.title}
                className="flex items-center gap-3 border-b border-dashed border-amber-200/80 py-1.5 last:border-b-0 sm:gap-4 sm:py-2"
              >
                <span
                  aria-hidden="true"
                  data-fx={`box-${i}`}
                  className="relative h-[18px] w-[18px] shrink-0 rounded-[5px] border-2 border-neutral-300 bg-white"
                >
                  <svg viewBox="0 0 24 24" fill="none" className="absolute -right-2 -top-2.5 h-6 w-6 overflow-visible text-emerald-500">
                    <path
                      data-fx={`tick-${i}`}
                      d="M4.5 13.5l4.5 4.5L20.5 5"
                      stroke="currentColor"
                      strokeWidth={3.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div data-fx={`row-${i}`} className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-xl sm:h-8 sm:w-8", ACCENTS[row.accent].chip)}
                  >
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold leading-tight text-neutral-800">{row.title}</p>
                    <p className="mt-0.5 hidden truncate text-xs text-neutral-500 sm:block">{row.detail}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
